'use client'

import { useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import {
  installResultsBackNavigationGuard,
  clearInterviewResultsPageActive,
  isInterviewResultsPath,
} from '@/lib/resultsBackNavigationGuard'

/**
 * Installs capture-phase popstate handler once and clears the results-session flag
 * when the user leaves /results via client navigation (Link, router.push, etc.).
 */
export default function ResultsBackNavigationGuard() {
  const pathname = usePathname()
  const prevPathname = useRef<string | null>(null)

  useEffect(() => {
    installResultsBackNavigationGuard()
  }, [])

  // Full document unload from /results (typed URL, external nav, refresh) — avoid stale session flag
  useEffect(() => {
    const clearIfLeavingResults = () => {
      try {
        if (isInterviewResultsPath(window.location.pathname)) {
          clearInterviewResultsPageActive()
        }
      } catch {
        // ignore
      }
    }
    window.addEventListener('beforeunload', clearIfLeavingResults)
    return () => window.removeEventListener('beforeunload', clearIfLeavingResults)
  }, [])

  useEffect(() => {
    const prev = prevPathname.current
    prevPathname.current = pathname
    if (prev != null && isInterviewResultsPath(prev) && !isInterviewResultsPath(pathname)) {
      clearInterviewResultsPageActive()
    }
  }, [pathname])

  return null
}
